"use client";


import React, { useState, useEffect, useCallback } from "react";
import { getCollectionByHandleApi } from "@/api/collection";
import type { IApiProduct } from "@/types/collection";
import ProductCard from "@/components/ui/product-card";
import type { IApiCollection } from "@/types/api";
import { useCart } from "@/contexts/cart-context";
import type { ICartProductInfo } from "@/types/cart";
import type { IAddToCartEvent } from "@/events";
import { EventType, publishEvent } from "@/events";
import { convertPaisaToRupees } from "@/libs/utils";
import { checkInventory } from "@/api/inventory";
import SortDropdown from "./SortDropdown";
import FilterComponent from "./FilterComponent";


export default function CollectionsClient() {
 const { addToCart } = useCart();
 const [collections, setCollections] = useState<IApiCollection | null>(null);
 const [isFilterOpen, setIsFilterOpen] = useState(false);
 const [isLoading, setIsLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [sortOption, setSortOption] = useState("featured");
 const [priceRange, setPriceRange] = useState<[number, number]>([0, 1399]);
 const [availability, setAvailability] = useState({
   inStock: true,
   outOfStock: false,
 });
 const [inventoryMap, setInventoryMap] = useState<Record<string, number>>({});
 const [filteredProducts, setFilteredProducts] = useState<IApiProduct[]>([]);


 // Function to handle page reload
 const handlePageReload = () => {
   window.location.reload();
 };


 // Set stock and out of stock values directly
 const inStockCount = collections?.products?.length || 0;
 const outOfStockCount = 0;


 // Handle add to cart
 const handleAddToCart = (product: IApiProduct) => {
   if (!product) {
     console.error("Product not available");
     return;
   }


   // Make sure the product has variants
   if (!product.variant || product?.variant?.length === 0) {
     console.error("Product variants not available");
     return;
   }

   // Get the selected variant ID
   const selectedVariant = product.variant;
   const variantId = selectedVariant?.id;
   if (!variantId) {
     console.error("Variant ID not available");
     return;
   }


   // Create minimal product info for display in cart
   const productInfo: ICartProductInfo = {
     title: product.title,
     price: String(selectedVariant?.price || product.price),
     image: product.image_url ? {
       id: product.id,
       url: product.image_url,
       alt_text: product.title,
       is_main: true,
       position: 1
     } : undefined,
     slug: product.handle,
     variant: {
       id: variantId,
       title: selectedVariant?.title || product.title,
     },
   };


   const event: IAddToCartEvent = {
     type: EventType.ADD_TO_CART,
     productId: product.id,
     productName: product.title,
     price: convertPaisaToRupees(+selectedVariant?.price),
     currency: "INR",
     timestamp: Date.now(),
     variant: selectedVariant?.title || "",
   };


   publishEvent(event);

   // KP tracking is now handled by the KpAdapter

   // Add to cart with variant ID and product info
   addToCart(variantId, 1, productInfo);
 };


 // Function to fetch collection data with filters and sorting
 const fetchCollectionData = useCallback(
   async (
     filterParams?: {
       priceRange?: [number, number];
       availability?: {
         inStock: boolean;
         outOfStock: boolean;
       };
     },
     sortParam?: string
   ) => {
     try {
       setIsLoading(true);
       setError(null);


       // Convert price range from rupees to paise (multiply by 100)
       const minPriceInPaise = (filterParams?.priceRange?.[0] || 0) * 100;
       const maxPriceInPaise = (filterParams?.priceRange?.[1] || 1399) * 100;


       const params: Record<string, string | number | boolean> = {
         sort: sortParam || "featured",
         min_price: minPriceInPaise,
         max_price: maxPriceInPaise,
       };


       // Always add the availability parameter
       if (filterParams?.availability) {
         // If inStock is selected, set available=true
         if (
           filterParams.availability.inStock &&
           !filterParams.availability.outOfStock
         ) {
           params.available = true;
         }
         // If outOfStock is selected, set available=false
         else if (
           !filterParams.availability.inStock &&
           filterParams.availability.outOfStock
         ) {
           params.available = false;
         }
       } else {
         // Default to available=true if no filter is provided
         params.available = true;
       }


       // Use hardcoded collection name
       const collections = await getCollectionByHandleApi("ryze-gums", params);


       if (collections) {
         setCollections(collections);
         const products = collections.products || [];
         setFilteredProducts(products);


         // Get all SKUs from all variants
         const allSkus = products.reduce((skus: string[], product) => {
           if (product.variant?.sku) {
             skus.push(product.variant.sku);
           }
           return skus;
         }, []);


         // Check inventory for all SKUs in one call
         if (allSkus.length > 0) {
           const inventoryResponse = await checkInventory(allSkus);
           const newInventoryMap = inventoryResponse.response.reduce(
             (acc: Record<string, number>, item) => {
               acc[item.skuCode] = item.qty;
               return acc;
             },
             {}
           );
           setInventoryMap(newInventoryMap);
         }
       } else {
         setError("Invalid response format");
       }
     } catch (err) {
       setError("Failed to fetch products. Please try again later.");
       console.error("Error fetching products:", err);
     } finally {
       setIsLoading(false);
     }
   },
   []
 );


 // Handle filter changes
 const handleFilterChange = (filters: {
   priceRange: [number, number];
   availability: {
     inStock: boolean;
     outOfStock: boolean;
   };
 }) => {
   // Update local state
   setPriceRange(filters.priceRange);
   setAvailability(filters.availability);


   // Fetch data with new filters
   fetchCollectionData(filters, sortOption);
 };


 // Handle sort changes
 const handleSortChange = (option: string) => {
   setSortOption(option);


   // Fetch data with new sort option
   fetchCollectionData({ priceRange, availability }, option);
 };


 // Initial data fetch
 useEffect(() => {
   // Pass the default filters for the initial data fetch
   fetchCollectionData(
     {
       priceRange,
       availability,
     },
     sortOption
   );
 }, [fetchCollectionData, priceRange, availability, sortOption]);


 // Loading and error states
 if (isLoading) {
   return (
     <div className="container-custom px-4 sm:px-6 max-w-7xl py-8">
       <div className="text-center">Loading...</div>
     </div>
   );
 }


 if (error) {
   return (
     <div className="container-custom px-4 sm:px-6 max-w-7xl py-8">
       <div className="text-center text-red-500">{error}</div>
       <button
         onClick={handlePageReload}
         className="mt-4 px-4 py-2 bg-black text-white rounded"
       >
         Retry
       </button>
     </div>
   );
 }


 return (
   <div className="container-custom px-4 sm:px-6 max-w-7xl py-8">
     <h1 className="text-[29.75px] md:text-[35px] text-[#1C1D1D] font-ainslie text-center font-semibold mb-6">
       {collections?.title}
     </h1>


     {/* Filter and Sort Controls in a row */}
     <div className="flex justify-between items-stretch gap-2 mb-6">
       <FilterComponent
         isFilterOpen={isFilterOpen}
         setIsFilterOpen={setIsFilterOpen}
         priceRange={priceRange}
         availability={availability}
         inStockCount={inStockCount}
         outOfStockCount={outOfStockCount}
         onFilterChange={handleFilterChange}
       />


       <SortDropdown
         sortOption={sortOption}
         onSortChange={handleSortChange}
       />
     </div>


     <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
       {filteredProducts.map((product) => (
         <ProductCard
           key={product.id}
           title={product.title}
           price={product?.variant?.price || "0"}
           mrp={product?.variant?.compare_at_price || "0"}
           image={product.image_url || "/placeholder-product.png"}
           handle={product.handle}
           onAddToCart={() => handleAddToCart(product)}
           inStock={product.variant?.sku ? (inventoryMap[product.variant.sku] || 0) > 0 : false}
           isLoading={isLoading}
         />
       ))}
     </div>
   </div>
 );
}